import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { HotelRegistryTable } from '@/components/platform-admin/HotelRegistryTable';
import { SystemHealthCard } from '@/components/platform-admin/SystemHealthCard';
import { PlatformMetricsCard } from '@/components/platform-admin/PlatformMetricsCard';
import { Building2, PlusCircle, MessageSquare, Package } from 'lucide-react';
import TerminalFloorNav from '../../components/TerminalFloorNav';

const PlatformDashboardPage = () => {
  return (
    <div className="terminal-theme min-h-screen admin-theme">
      {/* Header */}
      <header className="sticky top-0 z-30 border-b bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Building2 className="h-6 w-6 text-[hsl(var(--web3-primary))]" />
              <div>
                <h1 className="text-xl font-bold heading-font">getbitsy.ai Platform Admin</h1>
                <p className="text-xs text-muted-foreground">Cross-hotel management & monitoring</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                asChild
              >
                <Link to="/platform-admin/enroll">
                  <PlusCircle className="h-4 w-4 mr-2" />
                  Enroll Hotel
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8 py-8">
        {/* Hero Section */}
        <div className="blockchain-header-gradient rounded-xl p-8 mb-8 border">
          <h2 className="text-3xl md:text-4xl font-bold heading-font mb-2">
            Platform Administration
          </h2>
          <p className="text-muted-foreground max-w-2xl">
            Monitor system health, manage hotel enrollment, and oversee the entire getbitsy.ai ecosystem.
          </p>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Link to="/platform-admin/enroll" data-testid="quick-enroll-card" className="no-underline">
            <Card className="card-hover-lift cursor-pointer h-full">
              <CardHeader>
                <PlusCircle className="h-8 w-8 text-[hsl(var(--web3-primary))] mb-2" />
                <CardTitle className="text-lg">Enroll New Hotel</CardTitle>
                <CardDescription>Add a hotel to the blockchain program</CardDescription>
              </CardHeader>
            </Card>
          </Link>

          <Link to="/platform-admin/hermes" data-testid="quick-hermes-card" className="no-underline">
            <Card className="card-hover-lift cursor-pointer h-full">
              <CardHeader>
                <MessageSquare className="h-8 w-8 text-amber-600 mb-2" />
                <CardTitle className="text-lg">Chat with Hermes</CardTitle>
                <CardDescription>Get AI-powered platform insights</CardDescription>
              </CardHeader>
            </Card>
          </Link>

          <Link to="/vendors" data-testid="quick-vendors-card" className="no-underline">
            <Card className="card-hover-lift cursor-pointer h-full">
              <CardHeader>
                <Package className="h-8 w-8 text-[#F7931A] mb-2" />
                <CardTitle className="text-lg">Vendor Protocol</CardTitle>
                <CardDescription>Manage vendors, Room SBTs & supply chain</CardDescription>
              </CardHeader>
            </Card>
          </Link>

          <Card className="card-hover-lift">
            <CardHeader>
              <Building2 className="h-8 w-8 text-blue-600 mb-2" />
              <CardTitle className="text-lg">Hotel Registry</CardTitle>
              <CardDescription>View and manage all hotels below</CardDescription>
            </CardHeader>
          </Card>
        </div>

        {/* Dashboard Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* System Health */}
          <SystemHealthCard />

          {/* Platform Metrics */}
          <PlatformMetricsCard />
        </div>

        <Separator className="my-8" />

        {/* Hotel Registry Table */}
        <HotelRegistryTable />
      </main>
      <TerminalFloorNav />
    </div>
  );
};

export default PlatformDashboardPage;
